import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import Sidebar from "../components/Sidebar";
import Header from "../components/AppBar";
import Footer from "../components/Footer";
import { Alert } from "@mui/lab";
import { Translate } from 'react-localize-redux';
import { useSherwoodUser } from '../hooks';
import { spacing } from "@mui/system";
import { connect } from 'react-redux';
import { fetchInvestigations } from '../redux/actions/investigationsActions';
import { updateLoadingRecords } from '../redux/actions/offlineActions';
import { signOut } from '../redux/actions/authActions';
import { addBreadcrumb } from '../utils/diagnostics/breadcrumbs';
import {
  Hidden,
  CssBaseline,
  Paper as MuiPaper,
  
  Typography,
  CircularProgress,
} from "@mui/material";
import Loader from '../components/Loader';

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
const withWidth = () => (WrappedComponent) => (props) => <WrappedComponent {...props} width="xs" />;

const drawerWidth = 258;

const GlobalStyle = createGlobalStyle`
  html,
  body,
  #root {
    height: 100%;
  }

  body {
    background: ${(props) => props.theme.palette.background.default};
  }

  .MuiCardHeader-action .MuiIconButton-root {
    padding: 4px;
    width: 28px;
    height: 28px;
  }
`;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const LoadingScreen = styled.div`
  position:absolute;
  height:100%;
  width:100%;
  z-index:1301;
  opacity:0.7;
  background-color:#fff;
`;

const LoadingMessage = styled.div`
  position:absolute;
  display:flex;
  align-items: center;
  justify-content:center;
  vertical-align:center;
  height:100%;
  width:100%;
  z-index:1301;
`;
const LoadingContainer = styled.div`
    width:20rem;
    height:20rem;
    display:flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

// The patient bulk download must block the whole viewport (sidebar and header
// included): most screens render empty or inconsistent data until it finishes,
// so navigating away mid-download only leads to broken views. position:fixed
// (unlike the offline overlay above) also covers scrolled content. z-index 1350
// sits above MUI Dialogs (1300) — the download runs at boot/investigation
// switch, so any dialog under it is stale anyway — but below Snackbars (1400)
// so transient notifications stay visible.
const BlockingOverlay = styled.div`
  position:fixed;
  top:0;
  left:0;
  display:flex;
  align-items: center;
  justify-content:center;
  height:100%;
  width:100%;
  z-index:1350;
  background-color:rgba(255,255,255,0.7);
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width:100%;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  
  flex: 1;
  background:${(props) => props.theme.palette.background.default}!important;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Dashboard = ({ children, routes, width, investigations, offline, patientsDownloading, patientsProgress }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const {isLoading, error} = useSherwoodUser();
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    // SHER-XYZ — forced password-change guard.
    // When an admin has just reset this user's password, the JWT carries mustChangePassword=true,
    // surfaced as `forceChangePassword=1` in localStorage by authService.js. Redirect to the
    // change-password page until the user picks their own password (which clears the flag).
    if (
        typeof window !== "undefined" &&
        localStorage.getItem("forceChangePassword") === "1" &&
        location.pathname !== "/auth/change-password"
    ) {
        return <Redirect to="/auth/change-password" />;
    }

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Diagnostics: subscribe to navigation events for breadcrumb capture.
    useEffect(() => {
        try {
            addBreadcrumb('nav', history.location?.pathname || '/', { route: history.location?.pathname });
            const unlisten = history.listen((location) => {
                try { addBreadcrumb('nav', location.pathname, { route: location.pathname }); } catch (_) { /* ignore */ }
            });
            return () => { try { unlisten(); } catch (_) { /* ignore */ } };
        } catch (_) { /* ignore */ }
    }, [history]);

    useEffect(() => {
        async function fetchRemoteInvestigations(){
            try {
                await dispatch(
                    fetchInvestigations()
                );
            } catch (err) {
                // SHER-217 — fetchInvestigations rejects on 401 "Researcher not found"
                // (deleted user, expired session, etc.). Without this catch the rejection
                // surfaced as an "Uncaught (in promise)" and the SPA stayed on the
                // "Downloading data" loader. Bounce the user to sign-in with a clean
                // localStorage so they can re-authenticate.
                //
                // SHER-404 — only a genuine 401 expels. A 403 here is transient
                // (mod_evasive / proxy can throw a spurious 403) and must NOT log
                // the user out, matching fetchUser's tri-state classification
                // (authService.js AUTH_PROBE + hooks/useSherwoodUser, the CHOM
                // network-expel fix of 2026-06-10). This endpoint returns the
                // researcher's own investigations, gated only by the JWT — a real
                // credential failure surfaces as 401, never 403 — so there is no
                // legitimate authorization 403 to honor here. Keep the session on
                // 403: a later real API call will get a 401 and expel then.
                const status = err?.status ?? err?.response?.status;
                if (status === 401) {
                    try {
                        addBreadcrumb('state', 'auth.fetchInvestigations.unauthorized', {
                            status,
                            errorMessage: (err?.errorMessage || err?.response?.data?.errorMessage || '').slice(0, 120),
                        });
                    } catch (_) { /* never break logout */ }
                    // SHER-217+SHER-215 (review follow-up) — dispatch the full signOut
                    // action rather than calling clearSession() inline. signOut runs
                    // clearSession + clearPatientsStore (IndexedDB) + dispatches AUTH_SIGN_OUT,
                    // so Redux state stays consistent with localStorage. Without this,
                    // subscribers to state.auth would still see the previous user logged in
                    // until SignIn mounts.
                    try { await dispatch(signOut()); } catch (_) { /* never block redirect */ }
                    history.replace('/auth/sign-in');
                }
            }
        }
        if(!investigations.data){
            fetchRemoteInvestigations()
        }
        try{
          //const channel = new BroadcastChannel('sw-messages');
          //console.log(channel);
          //channel.addEventListener("message", async (event) => {
            
            
          navigator.serviceWorker.addEventListener('message', async event => {
              console.log("Message received", event);
              if(event.data.hasOwnProperty("updatingRecords")){
                  console.log(event.data.updatingRecords);
                  await dispatch( 
                      updateLoadingRecords(event.data.updatingRecords)
                  );
                  if(event.data.updatingRecords === false){
                    window.location.href = window.location.origin;
                  }
              }
          }, false);
        }
        catch(error){
          //postErrorSlack("", "BroadcastChannel not available", "");
          console.log("BroadcastChannel not available");
        }
        
    }, [])
    if(isLoading){
        return <Loader />
    }
    else if(error){
        return(
            <Alert mb={4} severity="error">
                <Translate id="investigation.share.error.description" />
            </Alert>
        ) 
    }
    const showSidebar = (investigations.data && investigations.currentInvestigation && import.meta.env.VITE_APP_PRODUCT === 'HOSPITAL') || (import.meta.env.VITE_APP_PRODUCT !== 'HOSPITAL')
    return (
        <React.Fragment>
          {
            patientsDownloading &&
            <BlockingOverlay role="alert" aria-busy="true">
                <LoadingContainer>
                    <Typography variant="subtitle1" color="textPrimary">
                        <Translate id="general.loading-patients" />
                    </Typography>
                    {
                      patientsProgress != null && patientsProgress.total > 0 &&
                      <Typography variant="subtitle1" color="textPrimary">
                          {`${patientsProgress.loaded.toLocaleString()} / ${patientsProgress.total.toLocaleString()} (${patientsProgress.percentage}%)`}
                      </Typography>
                    }
                    <CircularProgress />
                </LoadingContainer>
            </BlockingOverlay>
          }
          {
            offline.loading &&
            <React.Fragment>
                <LoadingScreen />
                <LoadingMessage>
                  <LoadingContainer>
                      <Typography variant="subtitle1" color="textPrimary">
                          <Translate id="general.updating-records" />
                      </Typography>
                      <CircularProgress />  
                  </LoadingContainer>
                </LoadingMessage>
            </React.Fragment>
          }
            
            <Root>
                
                <CssBaseline />
                <GlobalStyle />
                <Drawer>
                    <Hidden mdUp implementation="js">
                    {
                        (showSidebar) &&
                        <Sidebar
                          routes={routes}
                          PaperProps={{ style: { width: drawerWidth } }}
                          variant="temporary"
                          investigation={investigations.currentInvestigation}
                          open={mobileOpen}
                          onClose={handleDrawerToggle}
                      />    
                    }
                    
                    </Hidden>
                    <Hidden mdDown implementation="css">
                    {
                        (showSidebar) &&
                        <Sidebar
                            routes={routes}
                            investigation={investigations.currentInvestigation}
                            PaperProps={{ style: { width: drawerWidth } }}
                        />
                    }
                    </Hidden>
                </Drawer>
                <AppContent>
                    <Header onDrawerToggle={handleDrawerToggle} />
                    <MainContent sx={{
    height: {
      xs: '30%',
      md: '100%',
    }
  }}>
                    {children}
                    </MainContent>
                    <Footer />
                </AppContent>
                
            </Root> 
          </React.Fragment>
    );
};

const mapDispatchToProps = dispatch => {
    return {    
        fetchInvestigations : () => dispatch(fetchInvestigations())
    }
}

const mapStateToProps = (state) =>{
    return {
        investigations : state.investigations,
        offline: state.offline,
        // Primitives + stable null so connect's shallow-compare skips re-rendering
        // the whole layout on unrelated patients-state changes (data can hold 50k rows).
        patientsDownloading: state.patients.downloading,
        patientsProgress: state.patients.progress
    }
}
export default (connect(mapStateToProps, mapDispatchToProps)(Dashboard));
